import { useCallback, useMemo } from 'react';

import Fuse from 'fuse.js/min-basic';
import { uniqBy } from 'lodash-es';

import { useSearchPageData } from '#src/app/pages/search/pageContext';
import type { LocationItem } from '#src/app/pages/search/shared/types';
import { removeDiacritics } from '#src/app/shared/utils/string';

type Result = ModelHookResult<
	undefined,
	{
		searchForCountryName: (queryLocation: string, selectedLocation: string) => readonly LocationItem[];
	}
>;

export function useSearchForCountryName(): Result {
	const {
		data: { countries: pageCountries },
	} = useSearchPageData();

	const countryDisplayNameMap = useMemo(
		() => Object.fromEntries(pageCountries.map((c) => [c.countryCode, c.displayName])),
		[pageCountries],
	);

	const countries = useMemo(
		() => pageCountries.map((c) => ({ code: c.countryCode, name: c.displayName })),
		[pageCountries],
	);

	const fuseCountriesSet = useMemo(() => new Fuse(countries.map(({ name }) => name)), [countries]);

	return {
		actions: {
			searchForCountryName: useCallback(
				(queryLocation: string, selectedLocation: string) => {
					const filteredCountries = countries.filter((country) =>
						removeDiacritics(country.name.toLowerCase()).includes(removeDiacritics(queryLocation.toLowerCase())),
					);

					const codeExactMatch =
						queryLocation.length === 2
							? countries.filter((country) => country.code.toLowerCase() === queryLocation.toLowerCase())
							: [];

					const fuzzyCountryMatch = fuseCountriesSet.search(queryLocation);
					const filteredFuzzyCountries = fuzzyCountryMatch
						.map(({ item: suggestedCountry }) => countries.find(({ name }) => name === suggestedCountry))
						.filter((country) => !!country);

					const uniqueResults = uniqBy([...codeExactMatch, ...filteredCountries, ...filteredFuzzyCountries], 'code');
					return uniqueResults.slice(0, 3).map((country) => ({
						value: country.code,
						label: countryDisplayNameMap[country.code] || country.code,
						count: 0,
						isRefined: selectedLocation === country.code,
						type: 'country' as const,
					}));
				},
				[countries, countryDisplayNameMap, fuseCountriesSet],
			),
		},
	};
}
